import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { supabase } from "@/lib/supabase";
import type { Session, User } from "@supabase/supabase-js";

interface UpdateProfilePayload {
  fullName?: string;
  currentPassword?: string;
  newPassword?: string;
}

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  isPasswordRecovery: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  resetPassword: (
    email: string,
  ) => Promise<{ error: string | null; notFound?: boolean }>;
  updatePassword: (password: string) => Promise<{ error: string | null }>;
  updateProfile: (payload: UpdateProfilePayload) => Promise<{ error: string | null }>;
}

const AuthContext = createContext<AuthContextType | null>(null);

/** Translate common Supabase auth errors to Spanish */
function translateError(msg: string): string {
  const map: Record<string, string> = {
    "Invalid login credentials":
      "Correo electrónico o contraseña incorrectos.",
    "Email not confirmed":
      "Tu correo electrónico aún no ha sido confirmado. Revisa tu bandeja de entrada.",
    "User already registered":
      "Este correo electrónico ya está registrado.",
    "Password should be at least 6 characters":
      "La contraseña debe tener al menos 6 caracteres.",
    "Email rate limit exceeded":
      "Demasiados intentos. Por favor espera unos minutos e intenta de nuevo.",
    "For security purposes, you can only request this after 60 seconds":
      "Por seguridad, debes esperar 60 segundos antes de intentar de nuevo.",
    "Invalid redirect URL":
      "Error de configuración. Contacta a soporte@hazloconexcel.com.",
  };
  return map[msg] ?? "Ocurrió un error inesperado. Intenta de nuevo.";
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      if (event === "PASSWORD_RECOVERY") {
        setIsPasswordRecovery(true);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error: error ? translateError(error.message) : null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const resetPassword = async (email: string) => {
    // Verify the email exists in auth.users via RPC
    const { data: exists, error: rpcError } = await supabase.rpc(
      "check_email_exists",
      { lookup_email: email },
    );

    if (rpcError) {
      return { error: "Error al verificar el correo. Intenta de nuevo." };
    }

    if (!exists) {
      return {
        error: "El usuario no existe.",
        notFound: true,
      };
    }

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/`,
    });
    return { error: error ? translateError(error.message) : null };
  };

  const updatePassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    return { error: error ? translateError(error.message) : null };
  };

  const updateProfile = async ({
    fullName,
    currentPassword,
    newPassword,
  }: UpdateProfilePayload) => {
    const wantsPasswordChange = !!newPassword;
    const trimmedName = fullName?.trim();
    const currentName =
      (session?.user?.user_metadata?.full_name as string | undefined) ?? "";
    const wantsNameChange =
      trimmedName !== undefined && trimmedName !== currentName;

    if (!wantsPasswordChange && !wantsNameChange) {
      return { error: null };
    }

    if (wantsPasswordChange) {
      const email = session?.user?.email;
      if (!email) {
        return { error: "No se pudo identificar el usuario actual." };
      }
      if (!currentPassword) {
        return {
          error: "Debes ingresar tu contraseña actual para cambiarla.",
        };
      }
      const { error: verifyError } = await supabase.auth.signInWithPassword({
        email,
        password: currentPassword,
      });
      if (verifyError) {
        return { error: "La contraseña actual es incorrecta." };
      }
    }

    const updatePayload: Parameters<typeof supabase.auth.updateUser>[0] = {};
    if (wantsPasswordChange) updatePayload.password = newPassword;
    if (wantsNameChange) updatePayload.data = { full_name: trimmedName };

    const { error } = await supabase.auth.updateUser(updatePayload);
    return { error: error ? translateError(error.message) : null };
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        user: session?.user ?? null,
        loading,
        isPasswordRecovery,
        signIn,
        signOut,
        resetPassword,
        updatePassword,
        updateProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
